import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output, signal, TemplateRef } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxAvatar } from '../avatar/avatar';
import { HxMenuButtonComponent } from '../menu-button/menu-button/menu-button';
import { MenuButtonConfig, MenuItem } from '../menu-button/types/menu-config.types';

import { HxIcon } from '../icon/icon';
import { StatusIndicatorService } from '../status-indicator/status-indicator.service';
import { HxTooltipDirective } from '../tooltip/tooltip';
import { UserProfile } from './models/user-profile.interface';

/**
 * HxHeader - Reusable header component for ComplexCare applications
 *
 * Provides a consistent header layout with title, user profile, and action controls. Supports configurable dropdown
 * menus and interactive elements.
 *
 * @example
 *   ```html
 *   <hx-header
 *     [title]="'Dashboard'"
 *     [userProfile]="currentUser"
 *     [dropdownOptions]="userMenuOptions"
 *     (notificationClick)="handleNotifications()"
 *     (themeToggle)="toggleTheme()"
 *     (dropdownOptionClick)="handleMenuSelection($event)">
 *   </hx-header>
 *   ```;
 */
@Component({
  selector: 'hx-header',
  standalone: true,
  imports: [CommonModule, HxAvatar, HxMenuButtonComponent, HxIcon, HxTooltipDirective],
  templateUrl: './header.html',
  styleUrls: ['./header.css'],
  host: {
    class: 'hx-header',
    '[class]': 'cssClass()',
  },
})
export class HxHeader extends HxBaseControl {
  private readonly statusIndicator = inject(StatusIndicatorService);

  // Input Properties
  /** Application or page title (string) */
  title = input<string>();

  /** Custom title template */
  titleTemplate = input<TemplateRef<unknown> | null>(null);

  /** User profile information */
  userProfile = input<UserProfile>();

  /** Dropdown menu options */
  menuItems = input<MenuItem[]>([]);

  /** Whether to show the status indicator */
  showStatus = input<boolean>(false);

  protected readonly isOnline = this.statusIndicator.isOnline;
  protected readonly connectivityTooltip = computed(() =>
    this.isOnline() ? 'You are online.' : 'You are offline. Some actions may be limited.'
  );

  constructor() {
    super();
    this.statusIndicator.start();
  }

  // Output Events
  /** Emitted when menu item is selected */
  menuItemSelected = output<MenuItem>();

  /** Emitted when profile area is clicked without dropdown */
  profileClick = output<void>();

  // Internal State
  /** Whether avatar image failed to load */
  avatarLoadError = signal<boolean>(false);

  // Computed Properties
  /** Whether custom title template is provided */
  hasCustomTitle = computed(() => !!this.titleTemplate());

  /** Whether user profile section should be displayed */
  hasUserProfile = computed(() => !!this.userProfile());

  /** Whether dropdown functionality is available */
  hasDropdown = computed(() => this.menuItems().length > 0);

  /** Menu button configuration for profile dropdown */
  menuButtonConfig = computed((): MenuButtonConfig => {
    const items = this.menuItems();

    return {
      id: 'profile-menu',
      icon: 'chevron-down',
      items: items,
      showDividers: false,
    };
  });

  onProfileClick(): void {
    const profile = this.userProfile();
    if (!profile) return;

    this.profileClick.emit();
  }

  onMenuItemClick(item: MenuItem, index: number): void {
    if (item.disabled) return;

    this.menuItemSelected.emit(item);

    // Execute item action if provided
    if (item.action) {
      item.action();
    }
  }
}
